/**
 * MinimalHero.jsx — Granullo Hero System
 *
 * FOUNDER DIRECTIVE (LOCKED):
 * Two-column: LEFT = locked narrative + typewriter question + MONEY ≠ MISUSE + CTA + live metrics
 * RIGHT = auto-looping live system simulation panel (3 detailed real-world scenarios)
 *
 * Copy is FINAL. Layout is FINAL. This is the moment Granullo becomes unforgettable.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { EASE, DUR, heroStagger } from '../../lib/motionGrammar';
import { AmbientSignalField } from '../motion/AmbientSignalField';
import ImpactOrchestration from './ImpactOrchestration';
import { lensAnnotation, LENS_TYPES } from '../lens-v2/LensAnnotations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* TheoremOperator removed — replaced by atomic .hero-theorem-lockup CSS */

/* ═══════════════════════ STYLE MAP ═══════════════════════ */

/* ═══ STRICT 3-TIER INSTITUTIONAL COMMAND FEED HIERARCHY ═══
 * PRIMARY   #D0D0D0 — decision outcomes, blocked/allowed verdicts
 * SECONDARY #B0B0B0 — beneficiary, amount, merchant, condition detail
 * MUTED     #999999 — waiting states, applied rules labels, passive context
 */
const STYLE_COLORS = {
  thinking: '#999999',
  blocked: '#D0D0D0',
  success: '#D0D0D0',
  waiting: '#999999',
  waitingItem: '#999999',
  header: '#999999',
  rule: '#B0B0B0',
  info: '#B0B0B0',
  critical: '#D0D0D0',
};

/* ═══════════════════════ SIMULATION SCENARIOS ═══════════════════════ */

const SCENARIOS = [
  {
    user: 'Pay vendor $50,000 after delivery',
    phases: [
      { action: 'type', text: 'Evaluating conditions\u2026', style: 'thinking' },
      { action: 'type', text: 'Blocked \u2014 delivery not confirmed', style: 'blocked' },
      { action: 'reveal', text: 'Program rules applied:', style: 'header' },
      { action: 'stagger', items: [
        'Beneficiary: ABC Logistics',
        'Amount: $50,000 locked',
        'Merchant: Logistics vendors only',
        'Requires: Delivery confirmation + invoice match',
        'Approvals: Finance + Operations',
      ]},
      { action: 'type', text: 'Waiting on: delivery confirmation', style: 'waiting' },
      { action: 'type', text: 'Conditions satisfied. Execution permitted.', style: 'success' },
    ],
  },
  {
    user: 'Release wellness benefits to employees',
    phases: [
      { action: 'type', text: 'Evaluating conditions\u2026', style: 'thinking' },
      { action: 'type', text: 'Blocked \u2014 eligibility not verified', style: 'blocked' },
      { action: 'reveal', text: 'Program rules applied:', style: 'header' },
      { action: 'stagger', items: [
        'Beneficiaries: Approved employees',
        'Limit: $500 per employee',
        'Merchant: Health & wellness only',
        'Requires: Employment verification',
        'Expires: 30 days',
      ]},
      { action: 'type', text: 'Waiting on: eligibility verification', style: 'waiting' },
      { action: 'type', text: 'Conditions satisfied. Execution permitted.', style: 'success' },
    ],
  },
  {
    user: 'Distribute $2M grant',
    phases: [
      { action: 'type', text: 'Evaluating conditions\u2026', style: 'thinking' },
      { action: 'type', text: 'Blocked \u2014 3 conditions not met', style: 'blocked' },
      { action: 'reveal', text: 'Program rules applied:', style: 'header' },
      { action: 'stagger', items: [
        'Beneficiaries: 3,800 recipients',
        'Amount: $2,000,000 locked',
        'Requires: Identity + impact verification',
        'Approvals: Government + Auditor',
        'Release: milestone-based',
      ]},
      { action: 'reveal', text: 'Waiting on:', style: 'waiting' },
      { action: 'stagger', items: [
        'Identity verification (1,247 pending)',
        'Auditor approval',
        'Milestone validation',
      ], style: 'waitingItem' },
      { action: 'type', text: 'Partial release available: 32% eligible', style: 'info' },
      { action: 'type', text: '$1.36M never moved incorrectly', style: 'critical' },
      { action: 'type', text: 'Remaining locked until conditions are met', style: 'waiting' },
    ],
  },
];

/* ═══════════════════════ CONSTANTS ═══════════════════════ */

const CHAR_SPEED = 16;
const LINE_PAUSE = 180;
const COMPLETE_DELAY = 100;
const STAGGER_DELAY = 120;
const STAGGER_PAUSE = 180;
const REVEAL_DELAY = 120;
const FINAL_PAUSE = 4000;

const QUESTION = '\u201cWhat if money simply couldn\u2019t move incorrectly in the first place?\u201d';

/* ═══════════════════════ COMPONENT ═══════════════════════ */

export default function MinimalHero() {
  const [visible, setVisible] = useState(false);
  const [stats, setStats] = useState(null);
  const [animatedStats, setAnimatedStats] = useState({ controlled: 0, conditions: 0, programs: 0 });
  const navigate = useNavigate();

  /* Typewriter for question line */
  const [questionText, setQuestionText] = useState('');
  const [questionDone, setQuestionDone] = useState(false);

  /* Simulation state */
  const [scenarioIdx, setScenarioIdx] = useState(0);
  const [renderedItems, setRenderedItems] = useState([]);
  const [typingText, setTypingText] = useState('');
  const [typingStyle, setTypingStyle] = useState('');
  const [showUser, setShowUser] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [autoMode, setAutoMode] = useState(true);
  const [manualGrace, setManualGrace] = useState(false);
  const timersRef = useRef([]);
  const simContentRef = useRef(null);
  const hoveringRef = useRef(false);
  const autoRef = useRef(true);
  const graceTimerRef = useRef(null);

  /* Entry */
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  /* Hero ↔ Panel synchrony: dispatch strike-complete after MISUSE slash lands
   * statement delay: 0.8s + ~0.9s strike settle = ~1.7s after mount.
   * Panel listens and activates metric count-up + flow pulse ~400ms later.
   */
  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => {
      try { window.dispatchEvent(new CustomEvent('granullo:strike-complete')); } catch (e) { /* no-op */ }
    }, 1700);
    return () => clearTimeout(t);
  }, [visible]);

  /* Fetch live stats */
  useEffect(() => {
    fetch(`${API_URL}/api/program-control/stats`)
      .then(r => r.json())
      .then(data => setStats(data))
      .catch(() => {});
  }, []);

  /* Animated counter */
  useEffect(() => {
    if (!stats) return;
    const duration = 1500;
    const steps = 40;
    const interval = duration / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const ease = 1 - Math.pow(1 - step / steps, 3);
      setAnimatedStats({
        controlled: Math.round((stats.total_controlled || 0) * ease),
        conditions: Math.round((stats.conditions_enforced || 0) * ease),
        programs: Math.round((stats.programs_live || 0) * ease),
      });
      if (step >= steps) clearInterval(timer);
    }, interval);
    return () => clearInterval(timer);
  }, [stats]);

  /* Question typewriter — starts after delay */
  useEffect(() => {
    const startDelay = setTimeout(() => {
      let i = 0;
      const interval = setInterval(() => {
        i++;
        setQuestionText(QUESTION.slice(0, i));
        if (i >= QUESTION.length) {
          clearInterval(interval);
          setQuestionDone(true);
        }
      }, 35);
      return () => clearInterval(interval);
    }, 1200);
    return () => clearTimeout(startDelay);
  }, []);

  /* ═══ Simulation loop ═══ */
  useEffect(() => {
    const timers = [];
    const add = (fn, ms) => { timers.push(setTimeout(fn, ms)); };
    timersRef.current.forEach(clearTimeout);
    timersRef.current = timers;

    const scenario = SCENARIOS[scenarioIdx];
    setRenderedItems([]);
    setTypingText('');
    setTypingStyle('');
    setShowUser(false);

    let delay = 300;
    add(() => setShowUser(true), delay);
    delay += 500;

    for (const phase of scenario.phases) {
      if (phase.action === 'type') {
        const text = phase.text;
        const style = phase.style;
        const typeStart = delay;

        for (let i = 0; i < text.length; i++) {
          const slice = text.slice(0, i + 1);
          add(() => { setTypingText(slice); setTypingStyle(style); }, typeStart + i * CHAR_SPEED);
        }

        delay = typeStart + text.length * CHAR_SPEED + COMPLETE_DELAY;
        const capturedText = text;
        const capturedStyle = style;
        add(() => {
          setTypingText('');
          setTypingStyle('');
          setRenderedItems(prev => [...prev, { type: 'line', text: capturedText, style: capturedStyle }]);
        }, delay);
        delay += LINE_PAUSE;
      } else if (phase.action === 'reveal') {
        const capturedText = phase.text;
        const capturedStyle = phase.style;
        add(() => {
          setRenderedItems(prev => [...prev, { type: 'header', text: capturedText, style: capturedStyle }]);
        }, delay);
        delay += REVEAL_DELAY;
      } else if (phase.action === 'stagger') {
        const items = phase.items;
        const itemStyle = phase.style || 'rule';
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          const captured = itemStyle;
          add(() => {
            setRenderedItems(prev => [...prev, { type: captured, text: item }]);
          }, delay + i * STAGGER_DELAY);
        }
        delay += items.length * STAGGER_DELAY + STAGGER_PAUSE;
      }
    }

    delay += FINAL_PAUSE;
    add(() => {
      const tryAdvance = () => {
        if (!autoRef.current) return; // Manual mode — don't advance
        if (hoveringRef.current) {
          setTimeout(tryAdvance, 500);
        } else {
          setScenarioIdx(prev => (prev + 1) % SCENARIOS.length);
        }
      };
      tryAdvance();
    }, delay);

    return () => { timers.forEach(clearTimeout); timersRef.current = []; };
  }, [scenarioIdx]);

  /* Auto-scroll simulation content as lines appear */
  useEffect(() => {
    if (simContentRef.current) {
      simContentRef.current.scrollTo({ top: simContentRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [renderedItems, typingText]);

  const handleTryLive = useCallback(() => {
    navigate('/simulation-room?source=hero');
  }, [navigate]);

  const goToScenario = useCallback((idx) => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setScenarioIdx(idx);
    // Manual interaction: pause auto for 12s grace period
    if (autoRef.current) {
      setManualGrace(true);
      autoRef.current = false;
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = setTimeout(() => {
        autoRef.current = true;
        setManualGrace(false);
        setScenarioIdx(prev => (prev + 1) % SCENARIOS.length);
      }, 12000);
    }
  }, []);

  const goNext = useCallback(() => {
    goToScenario((scenarioIdx + 1) % SCENARIOS.length);
  }, [scenarioIdx, goToScenario]);

  const goPrev = useCallback(() => {
    goToScenario((scenarioIdx - 1 + SCENARIOS.length) % SCENARIOS.length);
  }, [scenarioIdx, goToScenario]);

  const toggleAuto = useCallback(() => {
    clearTimeout(graceTimerRef.current);
    setManualGrace(false);
    const next = !autoMode;
    setAutoMode(next);
    autoRef.current = next;
    if (next) {
      // Resume: advance to next after current finishes
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
      setScenarioIdx(prev => (prev + 1) % SCENARIOS.length);
    }
  }, [autoMode]);

  const handleSimHover = useCallback((entering) => {
    setIsHovering(entering);
    hoveringRef.current = entering;
  }, []);

  /* Derive ambient field mode from simulation state */
  const heroFieldMode = useMemo(() => {
    if (typingStyle === 'blocked' || typingStyle === 'critical') return 'alert';
    if (typingStyle === 'success') return 'calm';
    if (typingStyle === 'thinking' || typingStyle === 'waiting') return 'sensing';
    return 'default';
  }, [typingStyle]);

  return (
    <section
      className="relative flex items-center px-6 lg:px-16 overflow-hidden"
      style={{ minHeight: '92vh', background: 'var(--shell-bg)' }}
      data-testid="hero-section"
      {...lensAnnotation({
        id: 'hero-theorem',
        type: 'hero',
        title: 'Granullo Theorem: MONEY cannot equal MISUSE',
        meaning: 'The foundational thesis: when money carries structural proof, misuse becomes structurally impossible. Every payment creates a twin that knows what was prevented and what was created.',
        actions: ['Enter simulation room', 'Explore proof chain'],
        relations: ['payment-twin', 'granullo-pass', 'consequence-memory'],
        state: 'theorem-active',
        priority: 'high',
      })}
    >
      <AmbientSignalField lineCount={32} height={280} opacity={0.03} mode={heroFieldMode} />
      <div className="max-w-[1280px] mx-auto w-full grid min-[1260px]:grid-cols-[minmax(0,620px)_minmax(520px,1fr)] min-[1260px]:gap-[clamp(40px,4.2vw,72px)] gap-10 items-center py-20 min-[1260px]:py-0 relative" style={{ zIndex: 1 }}>

        {/* ═══ LEFT — 4-Block Narrative Hierarchy ═══ */}
        <div className="hero-left-column">

          {/* BLOCK 1 — Context line (QUIET INSTITUTIONAL MEMORY) */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={visible ? { opacity: 0.72, y: 0 } : {}}
            transition={{ duration: heroStagger.line1.duration, delay: heroStagger.line1.delay, ease: EASE.reveal }}
            style={{
              fontSize: 'clamp(18px, 1.4vw, 24px)',
              fontWeight: 400,
              lineHeight: 1.45,
              letterSpacing: '-0.01em',
              maxWidth: 620,
              color: 'var(--shell-text-secondary)',
              marginBottom: 28,
            }}
            data-testid="hero-line-1"
          >
            Systems have watched money.
            <br />
            After it has already moved.
          </motion.p>

          {/* BLOCK 2 — Primary question (SHARP SYSTEM CHALLENGE) */}
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={visible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: heroStagger.line2.duration, delay: heroStagger.line2.delay, ease: EASE.reveal }}
            className="hero-primary-question"
            data-testid="hero-line-3"
          >
            What if money simply couldn&rsquo;t move incorrectly in the first place?
          </motion.h1>

          {/* BLOCK 3 — Theorem proof stamp (MONEY ≠ MISUSE) — VISUAL PUNCHLINE */}
          <motion.div
            style={{ marginTop: 20, marginBottom: 18 }}
            initial={{ opacity: 0, y: 8 }}
            animate={visible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: heroStagger.statement.delay, ease: EASE.reveal }}
            data-testid="hero-tagline"
          >
            <div className="hero-theorem-lockup">
              <span className="theorem-money" data-testid="hero-money-word">MONEY</span>
              <span className="theorem-operator-wrap" data-testid="rebel-neq-glyph">
                <span className="theorem-equals">=</span>
                {/* Hand-drawn pressure-sensitive scarlet marker slash — editorial vandalism */}
                <svg
                  className="theorem-slash-svg theorem-slash-draw"
                  viewBox="0 0 16 76"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: '-6%',
                    left: '50%',
                    width: '0.09em',
                    height: '112%',
                    transform: 'translateX(-46%) rotate(21deg)',
                    overflow: 'visible',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}
                >
                  {/* Faint dry-brush ghost trail — 9% opacity secondary streak */}
                  <path
                    d="M 10 5 C 9.5 16, 11 30, 9 46 C 7.5 58, 10 68, 11 74"
                    fill="none"
                    stroke="var(--shell-scarlet, #CD2200)"
                    strokeWidth="0.8"
                    strokeLinecap="round"
                    opacity="0.09"
                  />
                  {/* Main marker stroke — variable width: thin entry, thick belly, tapered drag tail */}
                  <path
                    d="M 7.5 0
                       C 7 3, 6.2 8, 5.5 14
                       C 4.5 22, 3.6 30, 3.2 36
                       C 2.6 42, 2.8 48, 3.6 54
                       C 4.3 60, 5.2 66, 6 71
                       L 6.5 76
                       L 8.8 75.2
                       C 8.2 70, 9.2 64, 10.2 57
                       C 11.2 50, 11.8 43, 11.5 37
                       C 11 31, 10 24, 9.2 17
                       C 8.5 11, 8 5, 8.8 0
                       Z"
                    fill="var(--shell-scarlet, #CD2200)"
                  />
                </svg>
              </span>
              <span className="theorem-misuse" data-testid="hero-misuse-word">MISUSE</span>
            </div>
          </motion.div>

          {/* BLOCK 4 — Supporting line (CALM RESOLUTION) */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={visible ? { opacity: 0.78 } : {}}
            transition={{ duration: heroStagger.reinforcement.duration, delay: heroStagger.reinforcement.delay, ease: EASE.reveal }}
            style={{
              fontSize: 'clamp(16px, 1.2vw, 20px)',
              lineHeight: 1.45,
              color: 'var(--shell-text-muted)',
              fontWeight: 400,
              marginTop: 0,
              marginBottom: 36,
              maxWidth: 560,
            }}
            data-testid="hero-reinforcement"
          >
            Money cannot move where it shouldn&rsquo;t.
          </motion.p>


          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={visible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 1.2 }}
            className="mb-3"
          >
            <button
              onClick={handleTryLive}
              className="hero-cta-btn"
              data-testid="hero-cta"
            >
              See what would have moved <ArrowRight size={15} />
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={visible ? { opacity: 1 } : {}}
            transition={{ delay: 1.4 }}
            className="text-[12px] mb-8"
            style={{ color: 'var(--shell-text-faint)' }}
            data-testid="hero-microcopy"
          >
            Operates on the rails money already moves through
          </motion.p>

        </div>

        {/* ═══ RIGHT — Interactive Release Orchestration ═══ */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={visible ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.6 }}
        >
          <div
            className="hero-sim-panel hero-sim-panel--impact"
            data-testid="hero-simulation"
          >
            {/* Panel header — editorial frame, not UI chrome */}
            <div className="flex items-center px-7 py-4"
              style={{ borderBottom: '1px solid var(--ip-panel-border, rgba(15,15,15,0.05))' }}>
              <div className="flex items-center gap-2">
                <div className="w-[5px] h-[5px] rounded-full"
                  style={{ background: '#CD2200', boxShadow: '0 0 6px rgba(205,34,0,0.35)' }} />
                <span className="text-[9.5px] font-mono uppercase tracking-[0.16em]"
                  style={{ color: 'var(--ip-panel-muted, #8A8A8A)' }}>
                  Impact Orchestration
                </span>
              </div>
            </div>

            {/* Impact narrative content */}
            <div className="px-7 pt-6 pb-7 min-h-[380px] max-h-[640px] overflow-y-auto sim-scrollbar">
              <ImpactOrchestration />
            </div>
          </div>
        </motion.div>
      </div>

      <style>{heroStyles}</style>
    </section>
  );
}

/* ═══ Simulation Line Renderer ═══ */

function SimLine({ item, index }) {
  const isRule = item.type === 'rule';
  const isWaitingItem = item.type === 'waitingItem';
  const isHeader = item.type === 'header';
  const isIndented = isRule || isWaitingItem;
  const color = STYLE_COLORS[item.style] || STYLE_COLORS[item.type] || 'var(--g-text-soft, #3D3D3D)';

  return (
    <motion.div
      initial={{ opacity: 0, x: isIndented ? -4 : 0, y: isIndented ? 0 : 3 }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      transition={{ duration: 0.15 }}
      className={`flex items-start gap-2.5 ${isIndented ? 'pl-5' : ''} ${isHeader ? 'mt-2 mb-1' : ''}`}
    >
      {isIndented ? (
        <span className="text-[11px] mt-0.5 shrink-0" style={{ color: 'var(--g-muted-2)' }}>&bull;</span>
      ) : (
        <div className="w-1 h-3.5 rounded-full mt-0.5 shrink-0" style={{ background: color }} />
      )}
      <span
        className={`leading-relaxed ${isHeader ? 'text-[12px] font-mono uppercase tracking-[0.06em] font-medium' : 'text-[13px]'} ${item.style === 'critical' ? 'font-medium' : ''}`}
        style={{ color }}
      >
        {item.text}
      </span>
    </motion.div>
  );
}

/* ═══ Styles ═══ */

const heroStyles = `
  /* ═══ HERO LEFT COLUMN GOVERNANCE ═══ */
  .hero-left-column {
    max-width: 620px;
    min-width: 0;
    width: 100%;
    flex-shrink: 1;
    overflow: hidden;
  }

  /* ═══ HERO PRIMARY QUESTION — EDITORIAL SERIF ═══ */
  .hero-primary-question {
    font-family: "Source Serif 4", Georgia, serif;
    font-size: clamp(40px, 3.2vw, 52px);
    font-weight: 600;
    line-height: 1.05;
    letter-spacing: -0.03em;
    color: var(--shell-text);
    max-width: 820px;
    margin: 0;
  }

  /* ═══ HERO THEOREM ATOMIC FLEX LOCKUP ═══ */
  /* Single responsive typographic unit. Container font-size drives ALL children. */
  /* Wrapping is FORBIDDEN. The entire lockup scales proportionally as a whole. */
  .hero-theorem-lockup {
    display: inline-flex;
    align-items: baseline;
    gap: 0.14em;
    flex-wrap: nowrap;
    max-width: 100%;
    width: fit-content;
    min-width: 0;
    position: relative;
    isolation: isolate;
    overflow: visible;
    font-size: clamp(48px, 5.8vw, 96px);
  }
  .theorem-money {
    font-family: "Inter Tight", sans-serif;
    font-weight: 800;
    font-size: 0.72em;
    line-height: 0.92;
    letter-spacing: -0.06em;
    color: var(--shell-text);
    white-space: nowrap;
  }
  .theorem-operator-wrap {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 0.32em;
    height: 0.72em;
    flex-shrink: 0;
  }
  .theorem-equals {
    font-family: "Inter Tight", sans-serif;
    font-weight: 800;
    font-size: 0.58em;
    line-height: 1;
    color: var(--shell-text);
    transform: scaleY(1.1);
  }
  .theorem-slash-svg {
    pointer-events: none;
    z-index: 1;
  }
  /* Hand-drawn marker slash draw animation — single wrist motion reveal */
  .theorem-slash-draw {
    animation: slashDraw 220ms cubic-bezier(0.4, 0, 0.15, 1) 1.4s both;
  }
  @keyframes slashDraw {
    from { clip-path: inset(0 0 100% 0); }
    to { clip-path: inset(0 0 0% 0); }
  }
  .theorem-misuse {
    font-family: "Permanent Marker", cursive;
    font-size: 0.68em;
    line-height: 0.9;
    color: #CD2200;
    white-space: nowrap;
    transform: translateY(0.02em);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: geometricPrecision;
    paint-order: stroke fill;
  }

  /* ── Responsive: only adjust the container font-size ── */
  @media (max-width: 1280px) {
    .hero-primary-question { font-size: clamp(38px, 3vw, 48px); }
  }
  @media (max-width: 1024px) {
    .hero-primary-question { font-size: clamp(36px, 5vw, 52px); max-width: 100%; text-wrap: balance; }
    .hero-theorem-lockup { font-size: clamp(44px, 6.5vw, 84px); }
  }
  @media (max-width: 640px) {
    .hero-primary-question { font-size: clamp(28px, 7.5vw, 42px); }
    .hero-theorem-lockup { font-size: clamp(32px, 9vw, 56px); }
  }

  /* Granullo word with animated underline */
  .granullo-word {
    position: relative;
    display: inline-block;
  }
  .granullo-word::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: var(--shell-border-strong);
    border-radius: 1px;
    animation: drawUnderline 0.8s ease-out 0.9s forwards;
  }
  @keyframes drawUnderline {
    to { width: 100%; }
  }

  /* ≠ — monochrome, no glow */
  .neq-glow {
    color: var(--shell-text-muted);
    text-shadow: none;
    animation: none;
  }

  /* CTA button — Monochrome editorial: dark fill, institutional */
  .hero-cta-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 11px 24px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: -0.01em;
    color: var(--shell-nav-cta-text);
    background: var(--shell-nav-cta-bg);
    border: 1px solid transparent;
    box-shadow: none;
    cursor: pointer;
    transition: background 200ms ease, transform 200ms ease;
  }
  .hero-cta-btn:hover {
    transform: translateY(-1px);
    background: var(--shell-nav-cta-hover);
    color: var(--shell-nav-cta-text);
  }
  .hero-cta-btn:active {
    transform: translateY(0);
  }

  /* Simulation panel — COMMAND SURFACE: Always dark, cinematic, editorial */
  .hero-sim-panel {
    border-radius: 8px;
    background: #0A0A0A;
    border: 1px solid var(--shell-border);
    overflow: hidden;
    position: relative;
    color: #F0F0F0;
  }
  .hero-sim-panel::before {
    display: none;
  }
  /* Force dark internals on the simulation panel */
  .hero-sim-panel * {
    --g-border: var(--shell-border) !important;
    --g-surface: #1A1A1A !important;
    --g-surface-2: #111111 !important;
    --g-text: #E8E8E8 !important;
    --g-text-secondary: #B0B0B0 !important;
    --g-muted: #777777 !important;
    --g-muted-2: #777777 !important;
    --g-safe: #C7E3D4 !important;
  }

  /* Custom scrollbar for simulation */
  .sim-scrollbar::-webkit-scrollbar { width: 3px; }
  .sim-scrollbar::-webkit-scrollbar-track { background: transparent; }
  .sim-scrollbar::-webkit-scrollbar-thumb { background: rgba(15,23,42,0.06); border-radius: 3px; }

  /* Navigation buttons */
  .hero-nav-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 4px;
    border: none;
    background: rgba(15,23,42,0.04);
    color: rgba(15,23,42,0.3);
    cursor: pointer;
    transition: all 150ms ease;
  }
  .hero-nav-btn:hover {
    background: rgba(15,23,42,0.08);
    color: rgba(15,23,42,0.5);
  }

  @keyframes systemPulse {
    0%, 100% { opacity: 0.25; }
    50% { opacity: 0.5; }
  }
`;
